/**
 * PendingProposalsPanel — 25/02/2026
 *
 * Painel simétrico do DAF de cauções: mostra os leads com proposta
 * enviada mas ainda sem aceitação do cliente. Cada linha tem um campo
 * inline `pending_proposal_reason` que o Consultor(a) usa para
 * justificar por que o cliente ainda não aceitou (ex: "cliente pediu
 * tempo até 20/08 para consultar filha").
 *
 * Cores de aging por dias-desde-envio:
 *   info      · 0-3 dias
 *   warning   · 3-7 dias
 *   danger    · 7-14 dias
 *   critical  · >14 dias
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, AlertOctagon, MessageSquare, Save, ChevronRight, Users, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fmt = (n) => `${Number(n || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const daysLabel = (n) => (n === 0 ? "hoje" : n === 1 ? "há 1 dia" : `há ${n} dias`);

const bucketStyles = {
  info:    "bg-muted/50 border-border text-muted-foreground",
  warning: "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/30 text-[hsl(var(--warning))]",
  danger:  "bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/30 text-[hsl(var(--danger))]",
  critical:"bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/60 text-[hsl(var(--danger))] ring-1 ring-[hsl(var(--danger))]/30",
};
const rowTintBySev = {
  info: "",
  warning: "bg-[hsl(var(--warning-bg))]/30",
  danger: "bg-[hsl(var(--danger-bg))]/30",
  critical: "bg-[hsl(var(--danger-bg))]/50",
};

const REASON_TEMPLATES = [
  "Cliente pediu tempo para pensar",
  "A comparar com a concorrência",
  "Financeiro em análise",
  "A ultimar decisão familiar",
  "À espera de confirmação médica",
  "Aguarda visita para avaliar",
];

const ReasonEditor = ({ row, onSaved }) => {
  const [text, setText] = useState(row.reason_text || "");
  const [busy, setBusy] = useState(false);
  const dirty = text !== (row.reason_text || "");
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/leads/${row.id}/pending-proposal-reason`, { text });
      toast.success("Justificação guardada");
      onSaved(row.id, data.pending_proposal_reason);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally {
      setBusy(false);
    }
  };
  const applyTemplate = (tpl) => {
    // Se já há texto, appende; senão substitui. O consultor pode
    // sempre editar depois — os templates são um atalho, não uma
    // resposta pré-formatada.
    setText((prev) => {
      const cur = (prev || "").trim();
      if (!cur) return tpl;
      // Evita duplicar se o mesmo template já foi aplicado.
      if (cur.toLowerCase().includes(tpl.toLowerCase())) return cur;
      return `${cur} · ${tpl}`;
    });
  };
  return (
    <div className="flex items-start gap-2 w-full">
      <MessageSquare className="w-3.5 h-3.5 mt-1.5 text-muted-foreground/70 shrink-0" strokeWidth={1.5} />
      <div className="flex-1">
        <div
          className="flex flex-wrap gap-1 mb-1.5"
          data-testid={`reason-templates-${row.id}`}
        >
          {REASON_TEMPLATES.map((tpl) => (
            <button
              key={tpl}
              type="button"
              onClick={() => applyTemplate(tpl)}
              disabled={busy}
              className="text-[10px] px-2 py-0.5 rounded-full border border-border bg-card hover:bg-muted/60 hover:border-foreground/30 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              title="Aplicar como justificação"
              data-testid={`reason-template-${row.id}-${tpl.slice(0, 10)}`}
            >
              {tpl}
            </button>
          ))}
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Justifica porque o cliente ainda não aceitou (ou usa um dos motivos rápidos acima)…"
          rows={2}
          data-testid={`reason-input-${row.id}`}
          className="w-full bg-muted/40 border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/60 focus:border-primary/60 focus:outline-none resize-y min-h-[36px]"
          maxLength={2000}
        />
        <div className="flex items-center justify-between mt-1">
          <span className="text-[10px] text-muted-foreground">
            {row.reason_updated_by && row.reason_updated_at
              ? `Última: ${row.reason_updated_by} · ${new Date(row.reason_updated_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "short" })}`
              : "Sem justificação"}
          </span>
          {dirty && (
            <button
              type="button"
              onClick={save}
              disabled={busy}
              data-testid={`reason-save-${row.id}`}
              className="inline-flex items-center gap-1 text-[10px] font-semibold bg-foreground text-background rounded-full px-2.5 py-1 disabled:opacity-50 hover:bg-foreground/90 transition-colors"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Guardar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function PendingProposalsPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  // Filtro por consultor — só admin/direcao/dgo veem o dropdown.
  // Quando a API responde, `data.consultors` traz a lista popular o select.
  const [consultorId, setConsultorId] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const q = consultorId ? `?consultor_id=${encodeURIComponent(consultorId)}` : "";
        const r = await api.get(`/dashboard/pending-proposals${q}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(true);
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [consultorId]);

  const onReasonSaved = (leadId, reason) => {
    setData((prev) => prev ? {
      ...prev,
      pending_proposals: prev.pending_proposals.map((r) =>
        r.id === leadId ? { ...r, reason_text: reason.text, reason_updated_at: reason.updated_at, reason_updated_by: reason.updated_by_name } : r
      ),
    } : prev);
  };

  if (err) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 text-sm text-muted-foreground" data-testid="pending-proposals-error">
        Não foi possível carregar as propostas pendentes.
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 flex items-center gap-2 text-sm text-muted-foreground" data-testid="pending-proposals-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> A carregar propostas pendentes…
      </div>
    );
  }

  const rows = data.pending_proposals || [];
  const truncated = data.count > rows.length;
  const consultorsList = data.consultors || [];
  const canFilter = consultorsList.length > 0;   // Admin-like only (backend controla)

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8 mb-8" data-testid="pending-proposals-panel">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
            <AlertOctagon className="w-4 h-4 text-foreground" />
          </div>
          <div>
            <h3 className="font-ui text-lg text-foreground">Propostas por aceitar</h3>
            <div className="text-xs text-muted-foreground">Clientes que receberam proposta mas ainda não adjudicaram · Consultor justifica cada caso</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs flex-wrap">
          {canFilter && (
            <div className="flex items-center gap-1.5" data-testid="pp-consultor-filter">
              <Users className="w-3.5 h-3.5 text-muted-foreground" />
              <select
                value={consultorId}
                onChange={(e) => setConsultorId(e.target.value)}
                className="text-xs bg-background border border-border rounded-full px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-foreground/20"
                data-testid="pp-consultor-select"
                title="Filtrar por consultor"
              >
                <option value="">Todos os consultores</option>
                {consultorsList.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {consultorId && (
                <button
                  type="button"
                  onClick={() => setConsultorId("")}
                  className="text-muted-foreground hover:text-foreground p-0.5 rounded"
                  title="Limpar filtro"
                  data-testid="pp-consultor-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
          <span className="text-muted-foreground" data-testid="pending-proposals-count">
            {data.count} proposta{data.count === 1 ? "" : "s"} em aberto
          </span>
          <span className="text-foreground font-semibold tabular-nums">
            {fmt(data.amount_ref_total)}
          </span>
        </div>
      </div>

      {/* Aging band */}
      {(data.aging || []).length > 0 && (
        <div className="mb-5" data-testid="pending-proposals-aging">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold mb-2">
            Aging desde envio
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {data.aging.map((b) => (
              <div
                key={b.key}
                data-testid={`pp-aging-${b.key}`}
                className={`rounded-lg border p-3 ${bucketStyles[b.severity]} ${b.count === 0 ? "opacity-40" : ""}`}
              >
                <div className="text-[9px] uppercase tracking-[0.18em] font-bold opacity-80">{b.label}</div>
                <div className="font-ui text-2xl tracking-tight tabular-nums mt-1">{b.count}</div>
                <div className="text-[10px] tabular-nums opacity-80 mt-0.5">{fmt(b.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      {rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center" data-testid="pending-proposals-empty">
          Nenhuma proposta em aberto — parabéns!
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold" data-testid="pending-proposals-caption">
              A mostrar {rows.length}{truncated ? ` de ${data.count}` : ""} · Ordenadas por dias desde envio
            </div>
          </div>
          <div className="overflow-x-auto max-h-[640px] overflow-y-auto rounded-lg border border-border/60">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card z-10">
                <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold border-b border-border">
                  <th className="text-left py-2.5 px-3 w-[220px]">Cliente</th>
                  <th className="text-left py-2.5 px-3">Consultor(a)</th>
                  <th className="text-right py-2.5 px-3">Valor ref.</th>
                  <th className="text-right py-2.5 px-3">Enviado</th>
                  <th className="text-left py-2.5 px-3 w-[340px]">Justificação do Consultor</th>
                  <th className="py-2.5 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const tint = rowTintBySev[row.severity] || "";
                  return (
                    <tr key={row.id} className={`border-b border-border/60 last:border-b-0 hover:bg-muted/30 ${tint}`} data-testid={`pp-row-${row.id}`}>
                      <td className="py-3 px-3">
                        <div className="text-sm text-foreground truncate max-w-[220px]" data-testid={`pp-row-${row.id}-name`}>{row.name}</div>
                        {row.phone && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">{row.phone}</div>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <div className="text-xs text-foreground">{row.assigned_consultor_name || row.assigned_manager_name || "—"}</div>
                        {row.office && (
                          <div className="text-[10px] text-muted-foreground mt-0.5">{row.office}</div>
                        )}
                      </td>
                      <td className="py-3 px-3 text-right tabular-nums text-foreground">{fmt(row.amount_ref)}</td>
                      <td className="py-3 px-3 text-right">
                        <div className="text-xs text-foreground tabular-nums">
                          {row.proposal_last_sent_at
                            ? new Date(row.proposal_last_sent_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "short" })
                            : "—"}
                        </div>
                        <div className={`text-[10px] font-semibold ${
                          row.severity === "critical" ? "text-[hsl(var(--danger))]"
                            : row.severity === "danger" ? "text-[hsl(var(--danger))]"
                            : row.severity === "warning" ? "text-[hsl(var(--warning))]"
                            : "text-muted-foreground"
                        }`} data-testid={`pp-row-${row.id}-days`}>
                          {daysLabel(row.days_since_sent)}
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <ReasonEditor row={row} onSaved={onReasonSaved} />
                      </td>
                      <td className="py-3 px-3 text-right">
                        <Link
                          to={`/leads/${row.id}?tab=proposta`}
                          data-testid={`pp-open-${row.id}`}
                          className="inline-flex items-center gap-1 text-[11px] text-foreground hover:underline font-semibold"
                        >
                          Abrir <ChevronRight className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
